import type { UploadPurpose } from '@posilka/shared';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { uploadImage } from '@posilka/client/api/uploads';
import { haptic } from '@/lib/telegram';
import { Icon } from './Icon';

export interface UploadedPhoto {
  key: string;
  /** Lokal ko'rinish (object URL) — sahifa yangilansa yo'qoladi. */
  preview?: string;
}

interface Pending {
  id: string;
  preview: string;
  progress: number;
  error?: boolean;
}

/**
 * 1..max rasm: siqish → presigned yuklash, progress bilan (SPEC §11).
 * `capture` — shafyor olish rasmi uchun to'g'ridan-to'g'ri kamera.
 */
export function PhotoPicker({
  value,
  onChange,
  purpose,
  max = 3,
  capture,
}: {
  value: UploadedPhoto[];
  onChange: (photos: UploadedPhoto[]) => void;
  purpose: UploadPurpose;
  max?: number;
  capture?: boolean;
}) {
  const { t } = useTranslation();
  const [pending, setPending] = useState<Pending[]>([]);

  const add = async (files: FileList | null) => {
    if (!files) return;
    const room = max - value.length - pending.length;
    const selected = [...files].slice(0, Math.max(0, room));
    let current = value;
    await Promise.all(
      selected.map(async (file) => {
        const item: Pending = {
          id: crypto.randomUUID(),
          preview: URL.createObjectURL(file),
          progress: 0,
        };
        setPending((p) => [...p, item]);
        try {
          const key = await uploadImage(file, purpose, (progress) =>
            setPending((p) => p.map((x) => (x.id === item.id ? { ...x, progress } : x))),
          );
          current = [...current, { key, preview: item.preview }];
          onChange(current);
          setPending((p) => p.filter((x) => x.id !== item.id));
          haptic.success();
        } catch {
          haptic.error();
          setPending((p) => p.map((x) => (x.id === item.id ? { ...x, error: true } : x)));
        }
      }),
    );
  };

  const tile = 'relative h-24 w-24 shrink-0 overflow-hidden rounded-2xl bg-surface-2';

  return (
    <div className="flex flex-wrap gap-2">
      {value.map((p) => (
        <div key={p.key} className={tile}>
          {p.preview ? (
            <img src={p.preview} alt="" className="h-full w-full object-cover" />
          ) : (
            <span className="flex h-full items-center justify-center text-2xl">📷</span>
          )}
          <button
            type="button"
            aria-label={t('common.remove')}
            onClick={() => onChange(value.filter((x) => x.key !== p.key))}
            className="absolute top-1 right-1 flex h-6 w-6 items-center justify-center rounded-full bg-black/60 text-sm text-white"
          >
            ×
          </button>
        </div>
      ))}
      {pending.map((p) => (
        <div key={p.id} className={tile}>
          <img src={p.preview} alt="" className="h-full w-full object-cover opacity-50" />
          <div className="absolute inset-0 flex items-center justify-center text-sm font-semibold">
            {p.error ? (
              <button
                type="button"
                className="rounded bg-black/60 px-2 py-1 text-white"
                onClick={() => setPending((x) => x.filter((y) => y.id !== p.id))}
              >
                {t('photos.failed')}
              </button>
            ) : (
              <span className="rounded bg-black/60 px-2 py-0.5 text-white">
                {Math.round(p.progress * 100)}%
              </span>
            )}
          </div>
        </div>
      ))}
      {value.length + pending.length < max && (
        <label
          className={`${tile} press flex cursor-pointer flex-col items-center justify-center gap-1 border-2 border-dashed border-line text-muted`}
        >
          <Icon name={capture ? 'camera' : 'plus'} className="h-6 w-6" />
          <span className="px-1.5 text-center text-[11px] leading-tight break-words">
            {t(capture ? 'photos.takePhoto' : 'photos.add')}
          </span>
          <input
            type="file"
            accept="image/*"
            multiple={!capture && max > 1}
            {...(capture && { capture: 'environment' as const })}
            className="hidden"
            onChange={(e) => {
              void add(e.target.files);
              e.target.value = '';
            }}
          />
        </label>
      )}
    </div>
  );
}
